import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";

import { throwApiError } from "../../../api/api";
import type { RootState } from "../../../store/store";

import { getStoresPaginatedApi, StorePublic } from "../../../views/store/storeApi";
import { createUserApi, CreateUserRequest, UserType } from "../../../views/auth/authApi";
import ExtraRolesPicker, {
  sanitizeExtraRoles,
} from "../../../views/admin-sistema/components/ExtraRolesPicker";
import RevokedModulesPicker from "../../../views/admin-sistema/components/RevokedModulesPicker";
import { canGrantRole, SUPERVISOR_TYPES, USER_TYPE_OPTIONS } from "../../../views/admin-sistema/components/userAccess";
import { formatPhoneInput, isPhoneValid, phoneToApi } from "../../../utils/phone";
import type { AccessModule } from "../../../utils/accessModules";

import "../edit-user/EditUserModal.css";

const XIcon: React.FC = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

const SearchIconSvg: React.FC = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="11" cy="11" r="8" />
    <line x1="21" y1="21" x2="16.65" y2="16.65" />
  </svg>
);

const ChevronLeftIcon: React.FC = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="15 18 9 12 15 6" />
  </svg>
);

const ChevronRightIcon: React.FC = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="9 18 15 12 9 6" />
  </svg>
);

type Props = {
  isOpen: boolean;
  onClose: () => void;
  onCreated?: () => void;
};

const STORES_LIMIT = 4;

const onlyDigits = (value: string) => value.replace(/\D/g, "");

const formatCpf = (cpf: string) => {
  const d = onlyDigits(cpf).slice(0, 11);
  if (d.length <= 3) return d;
  if (d.length <= 6) return `${d.slice(0, 3)}.${d.slice(3)}`;
  if (d.length <= 9) return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6)}`;
  return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6, 9)}-${d.slice(9)}`;
};

const RequiredMark: React.FC = () => (
  <span style={{ color: "red", marginLeft: 4 }} aria-hidden="true">
    *
  </span>
);

const CreateUserModal: React.FC<Props> = ({ isOpen, onClose, onCreated }) => {
  const token = useSelector((state: RootState) => state.auth.accessToken);

  // Form
  const [nomeCompleto, setNomeCompleto] = useState("");
  const [cpf, setCpf] = useState("");
  const [email, setEmail] = useState("");
  // Sem tipo padrão: quem cria escolhe (antes o modal nascia em Supervisor).
  const [userType, setUserType] = useState<UserType | "">("");
  const [phone, setPhone] = useState("");
  const [whatsappOptIn, setWhatsappOptIn] = useState(true);
  const [extraRoles, setExtraRoles] = useState<UserType[]>([]);
  const [revokedModules, setRevokedModules] = useState<AccessModule[]>([]);
  // Administrador restrito só concede o que ele mesmo tem (ADR 0027).
  const typeOptions = useMemo(() => USER_TYPE_OPTIONS.filter((o) => canGrantRole(token, o.value)), [token]);
  const canGrant = useCallback((role: UserType) => canGrantRole(token, role), [token]);
  const [password, setPassword] = useState("");
  const [passwordConfirm, setPasswordConfirm] = useState("");

  // Stores
  const [storesPage, setStoresPage] = useState(1);
  const [storesTotal, setStoresTotal] = useState(0);
  const [stores, setStores] = useState<StorePublic[]>([]);
  const [loadingStores, setLoadingStores] = useState(false);
  const [selectedStoreIds, setSelectedStoreIds] = useState<Set<string>>(new Set());
  const [storeSearchInput, setStoreSearchInput] = useState("");
  const [storeSearch, setStoreSearch] = useState("");

  // Saving
  const [saving, setSaving] = useState(false);

  const storesTotalPages = useMemo(
    () => Math.max(1, Math.ceil(storesTotal / STORES_LIMIT)),
    [storesTotal],
  );

  const resetForm = useCallback(() => {
    setNomeCompleto("");
    setCpf("");
    setEmail("");
    setUserType("");
    setPhone("");
    setWhatsappOptIn(true);
    setExtraRoles([]);
    setRevokedModules([]);
    setPassword("");
    setPasswordConfirm("");
    setSelectedStoreIds(new Set());
    setSaving(false);

    setStoresPage(1);
    setStoresTotal(0);
    setStores([]);
    setStoreSearchInput("");
    setStoreSearch("");
  }, []);

  const formErrors = useMemo(() => {
    const errors: string[] = [];

    if (!nomeCompleto.trim()) errors.push("Informe o nome completo.");

    const cpfDigits = onlyDigits(cpf);
    if (cpfDigits.length !== 11) errors.push("CPF inválido (deve ter 11 dígitos).");

    const emailTrimmed = email.trim();
    if (emailTrimmed && !emailTrimmed.includes("@")) {
      errors.push("E-mail inválido.");
    }

    if (!isPhoneValid(phone)) errors.push("Celular inválido: informe o DDD e o número.");

    if (!userType) errors.push("Informe o tipo de usuário.");
    if (!password) errors.push("Informe a senha.");
    if (!passwordConfirm) errors.push("Confirme a senha.");
    if (password && passwordConfirm && password !== passwordConfirm) {
      errors.push("Senha e confirmação não conferem.");
    }

    return errors;
  }, [nomeCompleto, cpf, email, phone, userType, password, passwordConfirm]);

  const canSubmit = useMemo(() => {
    if (saving) return false;
    return formErrors.length === 0;
  }, [saving, formErrors]);

  const fetchStores = useCallback(
    async (page: number, search: string) => {
      if (!token) return;

      try {
        setLoadingStores(true);

        const nome = search.trim();
        const data = await getStoresPaginatedApi(token, {
          page,
          limit: STORES_LIMIT,
          ...(nome ? { nome } : {}),
        });

        setStores(data.items);
        setStoresTotal(data.total);
      } catch {
        toast.error("Erro ao buscar lojas.");
      } finally {
        setLoadingStores(false);
      }
    },
    [token],
  );

  const applyStoreSearch = useCallback(() => {
    setStoresPage(1);
    setStoreSearch(storeSearchInput);
  }, [storeSearchInput]);

  useEffect(() => {
    if (!isOpen) return;

    resetForm();
  }, [isOpen, resetForm]);

  useEffect(() => {
    if (!isOpen || !token) return;

    fetchStores(storesPage, storeSearch);
  }, [isOpen, token, storesPage, storeSearch, fetchStores]);

  useEffect(() => {
    if (!isOpen) return;

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !saving) onClose();
    };

    document.addEventListener("keydown", onKeyDown);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = prevOverflow;
    };
  }, [isOpen, onClose, saving]);

  const handleOverlayClick = () => {
    if (!saving) onClose();
  };

  const handleModalClick: React.MouseEventHandler<HTMLDivElement> = (e) => {
    e.stopPropagation();
  };

  const toggleStore = (storeId: string) => {
    setSelectedStoreIds((prev) => {
      const next = new Set(prev);

      if (next.has(storeId)) {
        next.delete(storeId);
      } else {
        next.add(storeId);
      }

      return next;
    });
  };

  const handleSubmit: React.FormEventHandler<HTMLFormElement> = async (e) => {
    e.preventDefault();

    if (formErrors.length > 0) {
      toast.error(formErrors[0]);
      return;
    }

    if (!userType) return;
    const emailTrimmed = email.trim();
    const hasStores = SUPERVISOR_TYPES.includes(userType);

    const dto: CreateUserRequest = {
      nomeCompleto: nomeCompleto.trim(),
      cpf: onlyDigits(cpf),
      userType,
      extraRoles: sanitizeExtraRoles(userType, extraRoles).filter(canGrant),
      ...(userType === "ADMIN_RESTRITO" ? { revokedModules } : {}),
      password,
      passwordConfirm,
      storeIds: hasStores && selectedStoreIds.size > 0 ? Array.from(selectedStoreIds) : undefined,
      phone: phoneToApi(phone),
      whatsappOptIn,
      ...(emailTrimmed ? { email: emailTrimmed } : {}),
    };

    try {
      setSaving(true);

      await createUserApi(dto);

      toast.success("Usuário criado com sucesso.");
      onCreated?.();
      onClose();
    } catch (error) {
      try {
        throwApiError(error, "Erro ao criar usuário.");
      } catch (e: any) {
        toast.error(e?.message ?? "Erro ao criar usuário.");
      }
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  const isLoadingStores = loadingStores;

  return (
    <div className="EditSupervisorOverlay" onClick={handleOverlayClick} role="presentation">
      <div className="EditSupervisorShell" onClick={handleModalClick} role="dialog" aria-modal="true">
        
        {/* Header */}
        <div className="EditSupervisorHeader">
          <div className="EditSupervisorHeaderLeft">
            <div>
              <h2 className="EditSupervisorHeaderTitle">Novo usuário</h2>
              <p className="EditSupervisorHeaderSub">
                Cadastrar um novo usuário no sistema
              </p>
            </div>
          </div>
          <button
            type="button"
            className="EditSupervisorHeaderClose"
            onClick={onClose}
            title="Fechar"
          >
            <XIcon />
          </button>
        </div>

        <form className="EditSupervisorBody" onSubmit={handleSubmit}>
          
          {/* Inputs Box */}
          <div className="EditSupervisorFormBox">
            <p className="EditSupervisorFormBoxTitle">Dados do usuário</p>
            
            <div className="EditSupervisorFormGrid">
              <div>
                <label className="EditSupervisorFormLabel" htmlFor="nomeCompleto">
                  Nome completo <RequiredMark />
                </label>
                <input
                  id="nomeCompleto"
                  className="EditSupervisorFormInput"
                  value={nomeCompleto}
                  onChange={(e) => setNomeCompleto(e.target.value)}
                  disabled={saving}
                  placeholder="Ex.: Maria José"
                />
              </div>

              <div>
                <label className="EditSupervisorFormLabel" htmlFor="cpf">
                  CPF <RequiredMark />
                </label>
                <input
                  id="cpf"
                  className="EditSupervisorFormInput"
                  value={cpf}
                  onChange={(e) => setCpf(formatCpf(e.target.value))}
                  disabled={saving}
                  placeholder="Somente números"
                  inputMode="numeric"
                />
              </div>

              <div>
                <label className="EditSupervisorFormLabel" htmlFor="email">
                  E-mail
                </label>
                <input
                  id="email"
                  className="EditSupervisorFormInput"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={saving}
                  placeholder="ex.: maria@empresa.com"
                  type="email"
                  autoComplete="email"
                />
              </div>

              <div>
                <label className="EditSupervisorFormLabel" htmlFor="userType">
                  Tipo de usuário <RequiredMark />
                </label>
                <select
                  id="userType"
                  className="EditSupervisorFormInput"
                  value={userType}
                  onChange={(e) => {
                    const next = e.target.value as UserType;
                    setUserType(next);
                    setExtraRoles((prev) => sanitizeExtraRoles(next, prev));
                  }}
                  disabled={saving}
                >
                  <option value="" disabled>
                    Selecione o tipo
                  </option>
                  {typeOptions.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="EditSupervisorFormLabel" htmlFor="phone">
                  Celular (WhatsApp)
                </label>
                <input
                  id="phone"
                  className="EditSupervisorFormInput"
                  value={phone}
                  onChange={(e) => setPhone(formatPhoneInput(e.target.value))}
                  disabled={saving}
                  placeholder="(21) 99999-8888"
                  inputMode="tel"
                  autoComplete="tel"
                />
              </div>

              <div className="EditSupervisorCheckRow">
                <label>
                  <input
                    type="checkbox"
                    checked={whatsappOptIn && !!phone}
                    onChange={(e) => setWhatsappOptIn(e.target.checked)}
                    disabled={saving || !phone}
                  />
                  Receber avisos no WhatsApp
                </label>
                <small>{phone ? "Os mesmos avisos do sino do app." : "Informe o celular para receber no WhatsApp."}</small>
              </div>

              {userType ? (
              <div style={{ gridColumn: "1 / -1" }}>
                {userType === "ADMIN_RESTRITO" ? (
                  <RevokedModulesPicker value={revokedModules} onChange={setRevokedModules} disabled={saving} />
                ) : (
                  <ExtraRolesPicker
                    principal={userType}
                    value={extraRoles}
                    onChange={setExtraRoles}
                    disabled={saving}
                    canGrant={canGrant}
                  />
                )}
              </div>
              ) : null}

              <div>
                <label className="EditSupervisorFormLabel" htmlFor="password">
                  Senha <RequiredMark />
                </label>
                <input
                  id="password"
                  className="EditSupervisorFormInput"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={saving}
                  type="password"
                  autoComplete="new-password"
                  placeholder="••••••••"
                />
              </div>

              <div>
                <label className="EditSupervisorFormLabel" htmlFor="passwordConfirm">
                  Confirmar senha <RequiredMark />
                </label>
                <input
                  id="passwordConfirm"
                  className="EditSupervisorFormInput"
                  value={passwordConfirm}
                  onChange={(e) => setPasswordConfirm(e.target.value)}
                  disabled={saving}
                  type="password"
                  autoComplete="new-password"
                  placeholder="••••••••"
                />
              </div>
            </div>
          </div>

          {/* Stores Box: só os supervisores têm lojas vinculadas */}
          {userType && SUPERVISOR_TYPES.includes(userType) ? (
          <div className="EditSupervisorStoresBox">
            <p className="EditSupervisorFormBoxTitle">
              Lojas vinculadas (opcional) <b>({selectedStoreIds.size})</b>
            </p>

            <div className="EditSupervisorStoreSearchRow">
              <input
                className="EditSupervisorFormInput"
                value={storeSearchInput}
                onChange={(e) => setStoreSearchInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    applyStoreSearch();
                  }
                }}
                disabled={isLoadingStores || saving}
                placeholder="Nome da loja"
                style={{ flex: 1 }}
              />

              <button
                type="button"
                className="EditSupervisorSearchButton"
                onClick={applyStoreSearch}
                disabled={isLoadingStores || saving}
                title="Buscar"
                style={{ height: 38 }}
              >
                <SearchIconSvg />
                Buscar
              </button>
            </div>

            {isLoadingStores ? (
              <div className="EditSupervisorTableEmpty">
                <p className="EditSupervisorEmptyText">Carregando lojas...</p>
              </div>
            ) : stores.length === 0 ? (
              <div className="EditSupervisorTableEmpty">
                <p className="EditSupervisorEmptyText">
                  {storeSearch.trim()
                    ? "Nenhuma loja encontrada para essa busca."
                    : "Nenhuma loja encontrada."}
                </p>
              </div>
            ) : (
              <div className="EditSupervisorStoresList">
                {stores.map((s) => {
                  const checked = selectedStoreIds.has(s.id);
                  return (
                    <button
                      key={s.id}
                      type="button"
                      className={`EditSupervisorStoreItem ${checked ? "checked" : ""}`}
                      onClick={() => toggleStore(s.id)}
                      disabled={saving}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        readOnly
                        className="EditSupervisorStoreCheckbox"
                      />
                      <div className="EditSupervisorStoreInfo">
                        <p className="EditSupervisorStoreName">{s.nomeFantasia}</p>
                        <p className="EditSupervisorStoreAddr">
                          {s.endereco ? s.endereco : "Endereço não informado"}
                        </p>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}

            {/* Stores Pagination */}
            <div className="EditSupervisorPaginationArea" style={{ marginTop: 0 }}>
              <p className="EditSupervisorPaginationInfo">
                Lojas página <span>{storesPage}</span> de <span>{storesTotalPages}</span>
              </p>
              
              <div className="EditSupervisorPaginationControls">
                <button
                  type="button"
                  disabled={isLoadingStores || storesPage <= 1 || saving}
                  onClick={() => setStoresPage((p) => Math.max(1, p - 1))}
                  className="EditSupervisorPaginationButton"
                >
                  <ChevronLeftIcon />
                </button>

                <button
                  type="button"
                  disabled={isLoadingStores || storesPage >= storesTotalPages || saving}
                  onClick={() => setStoresPage((p) => Math.min(storesTotalPages, p + 1))}
                  className="EditSupervisorPaginationButton"
                >
                  <ChevronRightIcon />
                </button>
              </div>
            </div>
            {userType === "SUPERVISOR_DE_CAMPO" ? (
              <p className="EditSupervisorEmptyText" style={{ marginTop: 8 }}>
                O supervisor de campo continua podendo avaliar qualquer loja; as vinculadas entram na cobrança semanal de visitas.
              </p>
            ) : null}
          </div>
          ) : null}

          {/* Footer buttons */}
          <div className="EditSupervisorModalActions">
            <button
              type="button"
              className="EditSupervisorModalButton Cancel"
              onClick={onClose}
              disabled={saving}
            >
              Cancelar
            </button>

            <button
              type="submit"
              className="EditSupervisorModalButton Confirm"
              disabled={!canSubmit}
              title={formErrors.length > 0 ? formErrors[0] : ""}
            >
              {saving ? "Salvando..." : "Criar usuário"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateUserModal;
